'use client'

import { useEffect, useLayoutEffect, useState, useRef, type FormEvent } from 'react'
import { AnimatePresence, motion } from 'motion/react'

import Link from 'next/link'
import Tooltip from '@/components/ui/Tooltip'
import Spliner from '@/lib/spline'
import ExternalIndicator from '@/components/ui/ExternalIndicator'

import { Google } from '@/components/icons/Google'
import { Icon } from '@/icons/Icon'
import { signInWithGoogle, requestMagicLink } from '@/app/(payload)/admin/login/actions'
import { DEAL_SIGNUP_VALUE_CENTS, trackFormSignup } from '@/lib/analytics'
import { WORK_EMAIL_ERROR_MESSAGE } from '@/lib/email-domain/blocklist'
import { getAccountOptInSourceForRedirect, markSignupSource } from '@/lib/auth/signup-source'
import { easeShort } from '@/lib/transitions'
import { cn, validateEmail } from '@/lib/utils'

type LoginMethod = 'email' | 'google'

// useLayoutEffect is a no-op on the server (and warns there), so fall back to
// useEffect during SSR. On the client this runs synchronously before paint,
// which we rely on below to avoid a signup->signin flash on mount.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

// Normalize a `?redirect=` value (or redirectUrl prop) into a safe relative
// path. Tolerates a missing leading slash (`courses/motion` -> `/courses/motion`)
// so a redirect isn't silently dropped, while still rejecting anything that
// could become an absolute / protocol-relative open redirect. Stricter than the
// server's sanitizeRelativeCallbackUrl on purpose — it never upgrades a value
// the server would reject into one it accepts.
function normalizeRelativeRedirect(value: string | null | undefined): string | null {
  if (!value) return null
  const trimmed = value.trim()
  if (!trimmed || trimmed.startsWith('//') || trimmed.includes('\\') || trimmed.includes('://')) {
    return null
  }
  const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  return path.startsWith('//') ? null : path
}

// We remember the last login method (to reorder the buttons + show the "last
// time" hint) and, only when the user opts in via "Remember email", their email
// (to pre-fill it). This lives in localStorage on purpose: it's pure client UX
// and the email never needs to travel to the server on every request.
const LAST_LOGIN_METHOD_KEY = 'auth:last-login-method' as const
const REMEMBERED_EMAIL_KEY = 'auth:remembered-email' as const

function readLoginPreferences(): { method: LoginMethod | null; email: string | null } {
  if (typeof window === 'undefined') return { method: null, email: null }
  try {
    const method = window.localStorage.getItem(LAST_LOGIN_METHOD_KEY)
    const email = window.localStorage.getItem(REMEMBERED_EMAIL_KEY)
    return {
      method: method === 'email' || method === 'google' ? method : null,
      email: email && email.includes('@') ? email : null,
    }
  } catch {
    return { method: null, email: null }
  }
}

function persistLoginPreferences(method: LoginMethod, rememberedEmail: string | null): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(LAST_LOGIN_METHOD_KEY, method)
    if (rememberedEmail) {
      window.localStorage.setItem(REMEMBERED_EMAIL_KEY, rememberedEmail)
    } else {
      window.localStorage.removeItem(REMEMBERED_EMAIL_KEY)
    }
  } catch {
    // localStorage unavailable (private mode / disabled) — preferences just
    // won't persist, which is a harmless degradation.
  }
}

export type AuthConfigProps = {
  signUp?: boolean
}

type AuthPortalProps = {
  // Where to land after auth completes. When rendered inside a modal there is
  // no ?redirect= search param to sniff, so the host passes the destination
  // directly (e.g. `/deals/motion?claim=true`). Must be a relative path.
  redirectUrl?: string
  className?: string
  config?: AuthConfigProps
}

export default function AuthPortal({
  redirectUrl: redirectUrlProp,
  className,
  config = { signUp: false },
}: AuthPortalProps = {}) {
  const [step, setStep] = useState<'start' | 'complete'>('start')
  const [mode, setMode] = useState<'signin' | 'signup'>(config.signUp ? 'signup' : 'signin')
  const [priority, setPriority] = useState<LoginMethod | null>(null)
  const [memory, setMemory] = useState(false)
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [focused, setFocused] = useState(false)
  const [loading, setLoading] = useState(false)
  // On Vercel preview deployments (any *.vercel.app host) Google OAuth is
  // unavailable — the preview's redirect_uri isn't registered in the Google
  // console. We hide the Google button on those hosts and steer users to
  // magic link only. (Password login was the workaround here, but the
  // AUTH_URL preview override in src/lib/auth/env-override.ts now makes
  // magic link work correctly on any branch.)
  const [isPreview, setIsPreview] = useState(false)
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null)
  const containerRef = useRef<HTMLFormElement>(null)

  useEffect(() => {
    const host = window.location.hostname
    if (host.includes('.vercel.app')) {
      setIsPreview(true)
    }

    const params = new URLSearchParams(window.location.search)
    const nextRedirect =
      normalizeRelativeRedirect(redirectUrlProp) ??
      normalizeRelativeRedirect(params.get('redirect'))
    if (nextRedirect) {
      setRedirectUrl(nextRedirect)
    }

    const urlError = params.get('error')

    // Don't let ?signup=true override a returning user's restored signin. The
    // layout effect above already forces signin when a saved login method exists
    // (and suppresses config.signUp for the same reason); honor that here too so
    // the URL param doesn't re-flip a known-account user to signup after paint.
    if (
      params.get('signup') === 'true' &&
      urlError !== 'no-account' &&
      !readLoginPreferences().method
    ) {
      setMode('signup')
    }
    if (urlError === 'email-mismatch') {
      setError('Google account email does not match the email you entered')
    } else if (urlError === 'no-account') {
      setError('No account found. Sign-ups are not available during beta.')
    } else if (urlError === 'not-admin') {
      setError('Admin credentials required to access the site during beta.')
    } else if (urlError === 'blocked-domain') {
      // EA-820: Google sign-up rejected because the email domain is blacklisted.
      setError(WORK_EMAIL_ERROR_MESSAGE)
    }
    if (urlError) {
      const cleanedParams = new URLSearchParams(window.location.search)
      cleanedParams.delete('error')
      const nextSearch = cleanedParams.toString()
      window.history.replaceState(
        {},
        '',
        nextSearch ? `${window.location.pathname}?${nextSearch}` : window.location.pathname,
      )
    }
  }, [redirectUrlProp])

  // Restore the saved login preferences: last method drives button ordering +
  // the "last time" hint; a remembered email pre-fills the field. When the last
  // method was email AND we have a remembered email, hasSavedEmail locks the
  // field into the "saved email" state with a "Use a different email" escape.
  useIsomorphicLayoutEffect(() => {
    const { method, email: savedEmail } = readLoginPreferences()
    if (method) {
      setPriority(method)
      // A prior login attempt means this is a returning user — default to
      // signin even if config.signUp requested the signup mode. Runs as a
      // layout effect so this resolves before paint, avoiding a visible
      // signup->signin flash when the modal mounts.
      setMode('signin')
    }
    if (savedEmail) {
      setEmail(savedEmail)
      setMemory(true)
    }
  }, [])

  async function handleEmailSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    setSuccess('')
    setLoading(true)

    // We no longer look the user up in the DB before sending the link. The
    // user's chosen mode tells us whether to treat this as a new signup, and
    // requestMagicLink rate-limits the send itself (magic-link: 3/email/15min).
    // Magic link works on every host (preview, production, local) — see
    // src/lib/auth/env-override.ts for why it's correct per-branch.
    try {
      const normalizedEmail = email.trim().toLowerCase()

      if (normalizedEmail.length > 100) {
        setError('Email address must be 100 characters or fewer')
        return
      }

      if (!validateEmail(normalizedEmail)) {
        setError('Please enter a valid email address')
        return
      }

      // EA-824: record the page this signup came from so the opt-in row matches
      // the newsletter form's pageUrl. Read server-side when the user is created.
      markSignupSource(
        getAccountOptInSourceForRedirect(normalizeRelativeRedirect(redirectUrl ?? redirectUrlProp)),
      )

      const result = await requestMagicLink(normalizedEmail, redirectUrl || undefined)
      if (result.success) {
        persistLoginPreferences('email', memory ? normalizedEmail : null)
        if (isSignUp) {
          // EA-821: value a deal-flow signup lower to avoid double-counting the
          // deal claim that triggered it.
          const isDealSignup = !!redirectUrl && redirectUrl.includes('/deals/')
          trackFormSignup(isDealSignup ? DEAL_SIGNUP_VALUE_CENTS : undefined)
        }
        switchToMode('complete')
      } else {
        setError(result.error || 'Failed to send magic link')
      }
    } catch {
      setError('An error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  function switchToMode(newStep: 'start' | 'complete') {
    setStep(newStep)
    setError('')
    setSuccess('')
    // Only reset the email/priority when returning to start. On 'complete' we
    // keep the email so the "A link has been sent to <email>" confirmation can
    // display it.
    if (newStep === 'start') {
      setEmail('')
      setPriority(null)
    }
  }

  function switchType(newMode: 'signin' | 'signup') {
    setError('')
    setSuccess('')
    setMode(newMode)
    setStep('start')
  }

  const isSignUp = mode === 'signup'
  // const isSignIn = mode === 'signin'

  const isStart = step === 'start'
  const isComplete = step === 'complete'

  const hasSavedEmail = memory && email !== '' && priority === 'email'

  const buttonClassFill =
    'flex h-10 w-full items-center justify-center rounded-full shadow-button-subtle bg-black px-3 text-14 font-semibold text-white'
  const buttonClassOutline =
    'flex h-10 w-full items-center gap-2.5 rounded-full border shadow-button-subtle border-gray-200 px-2.5 text-14 font-semibold text-gray-950 transition-colors hover:border-gray-300'

  let title: string
  let subtitle: string
  let emailButton: string
  let googleButton: string

  switch (mode) {
    case 'signin': {
      title = 'Sign in to your account'
      subtitle = 'Access your premium deals and Efficient App benefits'
      emailButton = 'Sign in with email'
      googleButton = 'Sign in with Google'
      break
    }
    case 'signup': {
      title = 'Create an account'
      subtitle = 'Unlock premium deals and access your Efficient App benefits'
      emailButton = 'Continue with email'
      googleButton = 'Continue with Google'
    }
  }

  return (
    <div className={cn('relative z-20 flex w-full max-w-[440px] flex-col gap-8', className)}>
      <div className="flex w-full flex-col items-center text-center">
        <Spliner
          scene="/splines/logo-primary.splinecode"
          fallback="/splines/logo-primary-fallback.png"
          loadStrategy="after-interaction-idle"
          showFallbackOnDesktop
        />
        <h1 className="mb-2 mt-8 font-heading text-24 font-bold leading-tight text-black sm:text-32">
          {isStart ? title : `Check your email`}
        </h1>
        {
          <AnimatePresence mode="wait">
            <motion.div
              key={mode}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={easeShort}
              className="mt-3 w-full text-13 text-gray-500"
            >
              {isStart ? (
                <p>{subtitle}</p>
              ) : (
                <div className="flex w-full flex-col gap-2">
                  <p>
                    We sent a secure link to <span className="font-bold">{email}</span>
                  </p>
                  {(() => {
                    const domain = email.trim().toLowerCase().split('@')[1] ?? ''
                    return domain === 'gmail.com' || domain === 'googlemail.com'
                  })() ? (
                    <Link
                      href="https://efficient.link/tools/search-email"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn(buttonClassOutline, 'group mt-6')}
                    >
                      <Google style={{ width: 18, height: 18 }} className="absolute" />
                      <div className="flex w-full items-center justify-center">
                        <span>Open Gmail</span>
                        <ExternalIndicator />
                      </div>
                    </Link>
                  ) : (
                    <p>Open the link to continue</p>
                  )}
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        }
      </div>

      <AnimatePresence>
        {!isComplete && (
          <motion.div
            key="complete"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={easeShort}
            className={cn('flex', priority === 'google' ? 'flex-col-reverse' : 'flex-col')}
          >
            <form
              ref={containerRef}
              onSubmit={handleEmailSubmit}
              className="flex flex-col items-center"
              noValidate
            >
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                }}
                onFocus={() => {
                  containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  setFocused(true)
                }}
                className="flex h-9 w-full items-center rounded-lg border border-transparent bg-gray-100 px-3 text-14 text-gray-950 placeholder-gray-400 transition-[active] focus:shadow-[0_0_0_1px_var(--colors-custom-efficient-500,#FF3465)_inset,0_0_0_2px_var(--colors-custom-efficient-100,#FFE2E7)] focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:text-gray-400"
                placeholder="Email"
                disabled={hasSavedEmail}
                required
                maxLength={100}
              />
              {error && (
                <p className="mt-2 w-full px-1 text-left text-13 text-error-500">{error}</p>
              )}
              <AnimatePresence>
                {focused && (
                  <motion.div
                    className="mt-2.5 flex w-full items-center justify-between px-1"
                    initial={{ opacity: 0, height: 0, marginTop: 0 }}
                    animate={{ opacity: 1, height: 'auto', marginTop: 10 }}
                    exit={{ opacity: 0, height: 0, marginTop: 0 }}
                    transition={easeShort}
                  >
                    <div
                      onClick={() => setMemory(!memory)}
                      className="group flex cursor-pointer items-center gap-3"
                    >
                      <span
                        className={cn(
                          'flex h-4 w-4 shrink-0 items-center justify-center rounded-key border transition-colors group-hover:border-gray-300',
                          memory ? 'border-black bg-black text-white' : 'border-gray-200 bg-white',
                        )}
                      >
                        {memory && <Icon name="check" size={12} strokeWidth={3} />}
                      </span>
                      <span className="text-13 leading-none text-gray-500">
                        Remember email on this device
                      </span>
                    </div>

                    <Tooltip
                      text="Saves your email on this device for faster sign-in. It does not keep you signed in."
                      position="up"
                      className="mt-1.5"
                      width={320}
                    >
                      <Icon name="circle-info" className="text-gray-400" size={16} />
                    </Tooltip>
                  </motion.div>
                )}
              </AnimatePresence>
              <button
                type="submit"
                disabled={loading}
                className={cn(buttonClassFill, 'mt-6 shadow-button-subtle')}
              >
                {loading ? (
                  <Icon
                    name="loader"
                    size={12}
                    strokeWidth={1}
                    className="animate-spin text-white"
                  />
                ) : (
                  emailButton
                )}
              </button>

              {priority === 'email' && (
                <span className="mt-3.5 text-12 text-gray-500">
                  You signed in with email last time
                </span>
              )}
            </form>

            <div className="flex h-16 items-center">
              <hr className="flex-1 border border-gray-100" />
              <span className="px-4 text-13 text-gray-500">or</span>
              <hr className="flex-1 border border-gray-100" />
            </div>

            <form action={signInWithGoogle} className="flex flex-col items-center">
              <input type="hidden" name="loginEmail" value={email} />
              {redirectUrl && <input type="hidden" name="callbackUrl" value={redirectUrl} />}
              <button
                type="submit"
                disabled={isPreview}
                onClick={() => {
                  const normalizedEmail = email.trim().toLowerCase()
                  persistLoginPreferences(
                    'google',
                    memory && normalizedEmail.includes('@') ? normalizedEmail : null,
                  )
                  // EA-824: record the originating page for the opt-in row. The
                  // cookie survives the Google OAuth round-trip (SameSite=Lax).
                  markSignupSource(
                    getAccountOptInSourceForRedirect(
                      normalizeRelativeRedirect(redirectUrl ?? redirectUrlProp),
                    ),
                  )
                }}
                className={cn(buttonClassOutline, isPreview && 'pointer-events-none opacity-40')}
              >
                <Google style={{ width: 18, height: 18 }} className="absolute" />
                <span className="w-full text-center">{googleButton}</span>
              </button>

              {priority === 'google' && (
                <span className="mt-3.5 text-12 text-gray-500">
                  You signed in with Google last time
                </span>
              )}
            </form>

            {isPreview && (
              <p className="mt-1 text-center text-12 text-gray-400">
                OAuth is unavailable on preview deployments
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        <motion.div
          key={`response-${error}-${success}`}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={easeShort}
          className="absolute top-full w-full pt-5 text-center text-13"
        >
          {success && <p className="text-success-500">{success}</p>}
        </motion.div>

        {isComplete || hasSavedEmail ? (
          <button
            key="auth-toggle"
            type="button"
            onClick={() => switchToMode('start')}
            className="mx-auto flex items-center gap-1.5 text-center text-13 text-gray-500 transition-colors hover:text-gray-950"
          >
            <Icon name="arrow-left" size={12} strokeWidth={1} /> Use a different email
          </button>
        ) : (
          <button
            key="auth-toggle"
            type="button"
            onClick={() => switchType(isSignUp ? 'signin' : 'signup')}
            className="block text-center text-13 text-gray-500 transition-colors hover:text-gray-950"
          >
            {isSignUp ? (
              <p>
                Already have an account? <span className="text-primary-500">Log in</span>
              </p>
            ) : (
              <p>Create an account</p>
            )}
          </button>
        )}

        <AnimatePresence key="terms-disclaimer">
          {isSignUp && !isComplete && (
            <motion.p
              className="text-center text-13 leading-relaxed text-gray-500"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={easeShort}
              style={{ overflow: 'hidden' }}
            >
              By continuing, you agree to our{' '}
              <Link
                href="/legal/terms"
                className="font-semibold transition-colors hover:text-gray-950"
              >
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link
                href="/legal/privacy"
                className="font-semibold transition-colors hover:text-gray-950"
              >
                Privacy Policy
              </Link>{' '}
              and will be subscribed to our newsletter. Unsubscribe any time.
            </motion.p>
          )}
        </AnimatePresence>
      </AnimatePresence>
    </div>
  )
}
